/**
 * Shared types for StatusFactors components
 */

import type {
  StatusFactorType,
  AggregationMethod,
  RollupWeighting,
  ArcAggregationMethod,
  ArcSelection,
  FactorMode,
  StatusValue,
  PopulationSource,
  RollupExclusion,
  RosterCoverage,
  RosterExclusions,
  RosterMember,
} from 'types/spherePoints';

export type { RosterExclusion, RosterExclusions } from 'types/spherePoints';
import type { VelocityBucketKey } from 'types/sphereMetrics';
import type { FilterExpression } from 'types/filters';
import type { CheckInScheduleValue, CheckInTriggerType } from '../components/molecules/CheckInScheduleEditor/CheckInScheduleEditorMolecule';
import type { PerspectiveFilterState } from '../components/molecules/PerspectiveFilter/PerspectiveFilterMolecule';
import type { SimpleFilterGroup } from './filterState';
import { type RollupSetModel, defaultRollupSet } from '../components/molecules/RollupSetCard/rollupSetModel';

/**
 * Unified form state for both new and existing factors
 */
export interface FactorFormState {
  // Identity (null for new factors until saved)
  factorId: string | null;

  // Common fields
  factorType: StatusFactorType | null;
  weight: number;
  name: string;
  description: string;
  /** Lifecycle mode — see `FactorMode`. Defaults to 'active'. */
  mode: FactorMode;
  /**
   * EAINT-750: whether this factor surfaces in per-factor drill-down views on
   * sphere-card interactions. Defaults to `true` for both new factors and
   * legacy factors that lack the property (see resolveShowOnCards on the read
   * side).
   */
  showOnCards: boolean;

  // Manual factor specific
  commentRequired: boolean;
  minCommentLength: number;
  checkInTrigger: CheckInTriggerType;
  checkInSchedule: Partial<CheckInScheduleValue>;
  /**
   * DB `system_name` of the currently-selected schedule template, `'custom'` for
   * the ad-hoc RRULE path, or null when nothing has been picked yet. Free-form
   * string because templates are DB-driven — see `check_in_schedule_templates`.
   */
  selectedScheduleTemplate: string | 'custom' | null;
  /**
   * Status the manual factor reports before its first check-in. Defaults to
   * 'unknown' so a brand-new factor is excluded from the sphere's blend
   * (see EAINT-748). Any other value makes the factor participate in the
   * blend at that reported status until the first check-in arrives.
   */
  initialStatus: StatusValue;

  // Arc health factor specific
  arcSelection: ArcSelection;
  arcAggregationMethod: ArcAggregationMethod;

  // Related spheres (Rollup) specific
  aggregationMethod: AggregationMethod;
  /** How the stored `aggregationMethod` pairs with how each member counts; see `combineModel`. */
  weighting: RollupWeighting;
  /** Status reported when the population is empty. Legacy traffic-light colours load as their status. */
  emptyBehavior: StatusValue;
  perspectiveDirection: 'inbound' | 'outbound';
  filterMode?: 'simple' | 'advanced';
  perspectiveFilters: PerspectiveFilterState[];
  propertyFilters: FilterExpression[];
  filterGroups?: SimpleFilterGroup[];
  /**
   * The Rollup Set Card's authoring model (EAINT-877) — scope, direction, narrowing tree, pins.
   * Supersedes the related-spheres fields above on save; see rollupSetModel.ts's compile-down
   * (Phase 5) for how the two are reconciled during the migration.
   */
  rollupSet: RollupSetModel;
  /**
   * True once this factor's population is authored through `rollupSet` rather than the legacy
   * `perspectiveDirection`/`perspectiveFilters`/`propertyFilters`/`filterGroups` fields above.
   * Gates `buildFactorPayload`'s compile-down (Phase 5). Since Phase 8 (D60), always `true` for
   * `factorType === 'related_spheres'`: `RelatedSpheresEditorMolecule` mounts only the Rollup
   * Set Card now, which reads and writes `rollupSet` exclusively, so a legacy factor kept on the
   * old (populationSources-absent) compile path would have its card edits silently discarded on
   * save — Phase 7's decompile reconstructs `rollupSet` from legacy `monitoringRules` too, so
   * routing every related_spheres factor through the new compile is safe. Defaults to `false`
   * for every other factor type, where the field is unused.
   */
  usesPopulationSources: boolean;

  // EAINT-875: engine-owned fields the form does not edit yet. Loaded from
  // the server so the write path can re-emit them verbatim, keeping every
  // field the backend already uses intact on a save. Superseded on the
  // related_spheres path by `rollupSet` + `usesPopulationSources` above
  // (EAINT-877 Phase 8); retained as ride-along storage for any residual
  // legacy read path and for the roster (EAINT-938) / exclusions
  // (EAINT-879) surfaces that will consume them as first-class form fields.
  /** Rollup sources beyond source[0]. The form drives source[0]; the tail is preserved verbatim. */
  preservedPopulationSourcesTail?: PopulationSource[];
  preservedMeasureSystemName?: string;

  // Rollup trust guardrails and edge cases. `null` is the off state.
  /** Smallest member influence the `worst` method considers (0–1); `null` = no floor. */
  influenceFloor: number | null;
  /** Share of the population that must report (`percent`, 0–100) before the factor reports; below it, `status`. `null` = no floor. */
  coverageFloor: { percent: number; status: StatusValue; exact?: number } | null;
  /** Perspective whose members count as children; `null` follows the lens. */
  childPerspective: string | null;
}

/**
 * Factor status for sidebar display.
 * Lifecycle states (deactivated/paused/observing/draft) take display priority over health values.
 * Health states are the real six StatusValue values (on_track/at_risk/needs_attention/critical/blocked/unknown).
 */
export type FactorStatus =
  // Lifecycle states (take priority over health)
  | 'deactivated'
  | 'paused'
  | 'observing'
  | 'draft'
  // Health states — real six-value StatusValue
  | 'on_track'
  | 'at_risk'
  | 'needs_attention'
  | 'critical'
  | 'blocked'
  | 'unknown';

/**
 * Local factor state with tracking metadata
 */
export interface LocalFactor {
  /** Unique identifier (temporary UUID for new factors) */
  id: string;
  /** True for drafts not yet persisted to server */
  isNew: boolean;
  /** True if modified since last apply/save */
  isDirty: boolean;
  /** True if has validation errors */
  hasValidationErrors: boolean;
  /** Validation error messages keyed by field */
  validationErrors: Record<string, string>;
  /** Current status for sidebar display */
  status: FactorStatus;
  /** Current local edits */
  localData: FactorFormState;
  /** Original server data (null for new factors) */
  originalData: FactorFormState | null;
  /** ISO 8601 timestamp when factor was deactivated (only for deactivated factors) */
  deactivatedAt?: string;
  /** User ID who deactivated the factor (only for deactivated factors) */
  deactivatedBy?: string;
  /** ISO 8601 timestamp when factor was paused (only for paused factors) */
  pausedAt?: string;
  /** User ID who paused the factor (only for paused factors) */
  pausedBy?: string;
  /**
   * Server-computed velocity — read-only display sidecar. Populated by
   * refreshFromServer when the factor has a stored velocity record.
   * Absent on drafts and on legacy factors that never recomputed.
   * Rendered by FactorSummaryMolecule as `velocity → <bucket>`.
   */
  velocityScore?: number;
  velocityBucket?: VelocityBucketKey;
  /**
   * Last-applied roster coverage for a `related_spheres` factor's rollup set — read-only
   * display sidecar, same shape as `velocityScore`/`velocityBucket`. Populated by
   * `refreshFromServer` from `factor.coverage`; absent on drafts and on factor types other
   * than `related_spheres`. Read by `ReadsAsBand` (via `FactorDetailPaneMolecule` /
   * `RelatedSpheresEditorMolecule`) — never client-recalculated.
   */
  coverage?: RosterCoverage;
  /**
   * Underlying canonical health status from the server, independent of
   * lifecycle overlay. Populated by refreshFromServer from
   * factor.currentStatus. `status` above collapses lifecycle modes
   * (observing/paused/deactivated) on top of health for sidebar display;
   * this preserves the real color for the "why this color?" chip in the
   * read-first summary, where the mode is already conveyed elsewhere.
   */
  currentStatus?: StatusValue;
}

/**
 * Validation result for a factor
 */
export interface FactorValidationResult {
  isValid: boolean;
  errors: Record<string, string>;
}

/**
 * Default values for a new factor form state
 */
export const DEFAULT_FACTOR_FORM_STATE: FactorFormState = {
  factorId: null,
  factorType: null,
  weight: 1,
  name: '',
  description: '',
  mode: 'active',
  showOnCards: true,
  commentRequired: false,
  minCommentLength: 0,
  checkInTrigger: 'adhoc',
  checkInSchedule: {
    triggerType: 'adhoc',
    responsiblePerspective: 'center_responsible',
  },
  selectedScheduleTemplate: null,
  initialStatus: 'unknown',
  arcSelection: { type: 'primary' },
  arcAggregationMethod: 'worst',
  aggregationMethod: 'worst',
  weighting: 'equal',
  emptyBehavior: 'unknown',
  influenceFloor: null,
  coverageFloor: null,
  childPerspective: null,
  perspectiveDirection: 'inbound',
  filterMode: 'simple',
  perspectiveFilters: [],
  propertyFilters: [],
  filterGroups: [],
  rollupSet: defaultRollupSet(),
  usesPopulationSources: false,
};

/**
 * Factor type display information
 */
export interface FactorTypeInfo {
  value: StatusFactorType;
  label: string;
  description: string;
  icon: string;
}

/** A roster row as the roster route serialises it — `RosterMember` plus the per-request derived fields. */
export interface FactorRosterMember extends Omit<RosterMember, 'sphereName'> {
  sphereName: string | null;
  /** Share of the blended weight among reporting members, 0–1. */
  share: number;
  /** True when this member's status determined a `worst`/`majority` result. */
  decisive: boolean;
  alsoRollsUpInto: Array<{ sphereId: string; sphereName: string; via: string }>;
}

export interface FactorRosterResponse {
  members: FactorRosterMember[];
  /** Rows matching the active filters — drives pagination, not the unfiltered set size. */
  total: number;
  hiddenCount: number;
  omittedCount: number;
  /** Author exclusions as the caller may see them; counts regardless of visibility. */
  exclusions: RosterExclusions;
  authoritative?: { status: string };
  projection: {
    scopeTotal?: number;
    coverage: RosterCoverage;
    distribution?: Record<StatusValue, number>;
    byMethod?: Record<AggregationMethod, StatusValue>;
    /** Why the projection landed where it did (e.g. `below_min_coverage`), from the stored config. */
    diagnostics?: string[];
    /** Reporting members whose chosen contribution factor no longer resolves. */
    contributionUnavailableCount?: number;
  };
  /** Members, across every page, whose WEIGHT is editable (manual-linked edge). Hidden members never count. */
  edgeBackedCount?: number;
  diagnostics?: string[];
}

export type FactorRosterStatusFilter = 'all' | 'measured' | 'not-yet-measured' | 'hidden' | 'broken';
export type FactorRosterSourceFilter = 'all' | PopulationSource['kind'];
export type FactorRosterSort = 'severity' | 'name' | 'contribution';
export type FactorRosterSortDir = 'asc' | 'desc';

/** Candidate rollup config for `POST …/factors/:factorId/simulate`; omitted keys inherit the stored value, `null` simulates a guardrail off. */
/** Unsaved combine settings the roster is computed under; absent fields keep the stored value, `null` floor means no floor. */
export interface RosterCombineOverrides {
  aggregationMethod?: AggregationMethod;
  weighting?: RollupWeighting;
  influenceFloor?: number | null;
}

export interface RollupSimulateRequest {
  aggregationMethod?: AggregationMethod;
  weighting?: RollupWeighting;
  influenceFloor?: number | null;
  minCoverage?: number | null;
  belowCoverageStatus?: StatusValue | null;
  childPerspective?: string | null;
  populationSources?: PopulationSource[];
  exclusions?: RollupExclusion[];
}

export interface RollupSimulateMethodResult {
  status: StatusValue;
  distribution?: Partial<Record<StatusValue, number>>;
  diagnostics?: string[];
}

export interface RollupSimulateResponse {
  status: StatusValue;
  coverage: RosterCoverage;
  distribution?: Partial<Record<StatusValue, number>>;
  byMethod?: Partial<Record<AggregationMethod, RollupSimulateMethodResult>>;
  diagnostics?: string[];
  /** The stored config evaluated over the same roster, so the headline compares like with like. */
  baseline: {
    status: StatusValue;
    coverage: RosterCoverage;
    diagnostics?: string[];
    contributionUnavailableCount?: number;
  };
}

export type { RollupExplanationResponse } from 'types/spherePoints';

export type { StatusOutputDestination, StatusOutputsResponse } from 'types/statusOutputs';
